import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { usePlivo } from "@/lib/plivo-context";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollText, GripHorizontal, X } from "lucide-react";

const STORAGE_KEY = "floating-call-script-pos";
const DEFAULTS = { x: 16, y: 80, w: 380, h: 420 };
const MIN_W = 280;
const MIN_H = 200;

interface WidgetState {
  x: number;
  y: number;
  w: number;
  h: number;
}

function loadState(): WidgetState {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    if (s) {
      const parsed = JSON.parse(s);
      if (
        typeof parsed.x === "number" &&
        typeof parsed.y === "number" &&
        typeof parsed.w === "number" &&
        typeof parsed.h === "number"
      ) {
        return parsed;
      }
    }
  } catch {}
  return { ...DEFAULTS };
}

function saveState(state: WidgetState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

export function FloatingCallScript() {
  const {
    callState,
    activeContactId,
    activeCallLogId,
    currentCallUuid,
    callDirection,
    activePdCampaignId,
    ourNumber,
  } = usePlivo();

  const [widget, setWidget] = useState<WidgetState>(loadState);
  const [dismissed, setDismissed] = useState(false);
  const [selectedScriptId, setSelectedScriptId] = useState<string | null>(null);

  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const isResizing = useRef(false);
  const resizeStart = useRef({ mx: 0, my: 0, w: 0, h: 0 });
  const widgetRef = useRef<WidgetState>(widget);

  useEffect(() => {
    widgetRef.current = widget;
  }, [widget]);

  const { data: tenantSettings } = useQuery<{ tenantBusinessSettings?: any }>({
    queryKey: ["/api/tenant-settings"],
  });

  const featureEnabled =
    tenantSettings?.tenantBusinessSettings?.screenPopEnableFloatingScript === true;

  const isActive =
    featureEnabled &&
    !!activeContactId &&
    (callState === "in-progress" || callState === "ringing" || callState === "incoming");

  const { data: contact } = useQuery<any>({
    queryKey: ["/api/contacts", activeContactId],
    enabled: !!activeContactId && isActive,
  });

  const { data: screenPopScripts } = useQuery<any[]>({
    queryKey: ["/api/screen-pop-scripts"],
    enabled: isActive,
  });

  const { data: activeCallLog } = useQuery<any>({
    queryKey: ["/api/call-logs", activeCallLogId],
    enabled: !!activeCallLogId && isActive,
  });

  const activeCampaignId =
    activeCallLog?.campaignId || (isActive ? activePdCampaignId : null) || null;

  const { data: activeCampaign } = useQuery<any>({
    queryKey: ["/api/campaigns", activeCampaignId],
    enabled: !!activeCampaignId && isActive,
  });

  const campaignScriptId = activeCampaign?.enableCallScript
    ? activeCampaign?.callScriptId || null
    : null;

  const { data: campaignScript } = useQuery<any>({
    queryKey: ["/api/screen-pop-scripts", campaignScriptId],
    enabled: !!campaignScriptId && isActive,
  });

  const { data: automationCallScript } = useQuery<{ script: string | null }>({
    queryKey: [`/api/automation-call-script/${activeContactId}`],
    enabled: !!activeContactId && isActive,
    staleTime: 0,
    refetchOnMount: "always",
  });

  // Reset per-call state when a new call starts.
  // Use currentCallUuid as the key because it is set for every call type
  // (regular and power dialer). activeCallLogId is always null for PD calls
  // since the server creates the log, so it cannot be used alone as the key.
  const prevCallKey = useRef<string | null>(null);
  useEffect(() => {
    const key = currentCallUuid;
    if (key !== prevCallKey.current) {
      setDismissed(false);
      setSelectedScriptId(null);
      prevCallKey.current = key;
    }
  }, [currentCallUuid]);

  const filteredScripts = useMemo(() => {
    if (!screenPopScripts || !Array.isArray(screenPopScripts)) return [];
    const activeQueueId = activeCallLog?.queueId || null;
    // Normalise our caller ID for comparison (strip leading +)
    const normalizedOurNumber = ourNumber ? ourNumber.replace(/^\+/, "") : "";
    const matched = screenPopScripts.filter((s: any) => {
      if (!s.isActive) return false;
      if (s.scriptType === "campaign") return false;
      if (s.scriptType === "both") return true;
      if (s.scriptType !== callDirection) return false;
      if (s.scriptType === "inbound" && s.queueId) {
        return s.queueId === activeQueueId;
      }
      // Outbound scripts with phone number restrictions: only show if caller ID matches
      if (s.scriptType === "outbound" && Array.isArray(s.phoneNumbers) && s.phoneNumbers.length > 0) {
        if (!normalizedOurNumber) return false;
        return s.phoneNumbers.some((pn: string) => pn.replace(/^\+/, "") === normalizedOurNumber);
      }
      return true;
    });
    matched.sort((a: any, b: any) => {
      // Phone-number-specific outbound scripts rank highest
      const aPN = a.scriptType === "outbound" && Array.isArray(a.phoneNumbers) && a.phoneNumbers.length > 0 ? 2 : 0;
      const bPN = b.scriptType === "outbound" && Array.isArray(b.phoneNumbers) && b.phoneNumbers.length > 0 ? 2 : 0;
      if (aPN !== bPN) return bPN - aPN;
      const aQ = a.queueId ? 1 : 0;
      const bQ = b.queueId ? 1 : 0;
      if (aQ !== bQ) return bQ - aQ;
      const aD = a.scriptType !== "both" ? 1 : 0;
      const bD = b.scriptType !== "both" ? 1 : 0;
      return bD - aD;
    });
    return matched;
  }, [screenPopScripts, callDirection, activeCallLog, ourNumber]);

  const automationScriptOverride = automationCallScript?.script || null;

  const activeScript = useMemo(() => {
    if (automationScriptOverride) {
      return {
        id: "__automation__",
        name: "Automation Script",
        content: automationScriptOverride,
        scriptType: "both",
        _source: "automation",
      };
    }
    if (campaignScript) {
      return { ...campaignScript, _source: "campaign" };
    }
    if (!filteredScripts.length) return null;
    if (selectedScriptId) {
      const found = filteredScripts.find(
        (s: any) => String(s.id) === selectedScriptId
      );
      if (found) return found;
    }
    return filteredScripts[0];
  }, [filteredScripts, selectedScriptId, automationScriptOverride, campaignScript]);

  const hasScriptContent = !!(
    automationScriptOverride ||
    campaignScript ||
    filteredScripts.length > 0
  );

  const showWidget = isActive && hasScriptContent && !dismissed;

  // ── Drag ────────────────────────────────────────────────────────────────────
  const handleDragMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("button, [role='combobox'], [role='listbox']")) return;
      isDragging.current = true;
      dragOffset.current = {
        x: e.clientX - widgetRef.current.x,
        y: e.clientY - widgetRef.current.y,
      };
      e.preventDefault();
    },
    []
  );

  // ── Resize ───────────────────────────────────────────────────────────────────
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    isResizing.current = true;
    resizeStart.current = {
      mx: e.clientX,
      my: e.clientY,
      w: widgetRef.current.w,
      h: widgetRef.current.h,
    };
    e.preventDefault();
    e.stopPropagation();
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging.current) {
        const newX = Math.max(
          0,
          Math.min(window.innerWidth - widgetRef.current.w, e.clientX - dragOffset.current.x)
        );
        const newY = Math.max(
          0,
          Math.min(window.innerHeight - 52, e.clientY - dragOffset.current.y)
        );
        setWidget((prev) => ({ ...prev, x: newX, y: newY }));
      } else if (isResizing.current) {
        const dx = e.clientX - resizeStart.current.mx;
        const dy = e.clientY - resizeStart.current.my;
        const newW = Math.max(MIN_W, resizeStart.current.w + dx);
        const newH = Math.max(MIN_H, resizeStart.current.h + dy);
        setWidget((prev) => ({ ...prev, w: newW, h: newH }));
      }
    };

    const handleMouseUp = () => {
      if (isDragging.current || isResizing.current) {
        isDragging.current = false;
        isResizing.current = false;
        saveState(widgetRef.current);
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  if (!showWidget) return null;

  const renderedContent = (() => {
    if (!activeScript?.content) return "";
    const nameParts = (contact?.name || "").split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";
    return (activeScript.content as string)
      .replace(/\{\{first_name\}\}/gi, firstName)
      .replace(/\{\{last_name\}\}/gi, lastName)
      .replace(/\{\{company\}\}/gi, contact?.company || "")
      .replace(/\{\{phone\}\}/gi, contact?.phoneNumber || "")
      .replace(/\{\{email\}\}/gi, contact?.email || "")
      .replace(/\{\{job_title\}\}/gi, contact?.jobTitle || "")
      .replace(/\{\{name\}\}/gi, contact?.name || "");
  })();

  const badgeLabel = activeScript
    ? (activeScript as any)._source === "automation"
      ? "Automation"
      : (activeScript as any)._source === "campaign"
        ? "Power Dialer"
        : activeScript.scriptType === "both"
          ? "All Calls"
          : activeScript.scriptType === "inbound"
            ? "Inbound"
            : "Outbound"
    : "";

  const contentMaxHeight = Math.max(MIN_H - 80, widget.h - 80);

  return (
    <div
      style={{
        position: "fixed",
        left: widget.x,
        top: widget.y,
        width: widget.w,
        zIndex: 9998,
        userSelect: "none",
      }}
      data-testid="floating-call-script"
    >
      <Card className="shadow-[0_8px_40px_rgba(0,0,0,0.35)] border overflow-hidden">
        {/* Drag handle / header */}
        <div
          className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-muted/50 cursor-grab active:cursor-grabbing"
          onMouseDown={handleDragMouseDown}
          data-testid="floating-call-script-handle"
        >
          <div className="flex items-center gap-1.5 min-w-0">
            <GripHorizontal className="w-4 h-4 text-muted-foreground shrink-0" />
            <ScrollText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <span className="text-xs font-semibold truncate">Call Script</span>
          </div>
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6 shrink-0"
            onClick={() => setDismissed(true)}
            data-testid="button-floating-script-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>

        {/* Content */}
        <CardContent className="p-3 space-y-2">
          {!activeScript ? (
            <p className="text-xs text-muted-foreground text-center py-2">
              No script available for {callDirection} calls.
            </p>
          ) : (
            <>
              {!automationScriptOverride && filteredScripts.length > 1 && (
                <Select
                  value={String(activeScript.id)}
                  onValueChange={(v) => setSelectedScriptId(v)}
                >
                  <SelectTrigger
                    className="h-7 text-xs"
                    data-testid="select-floating-script"
                  >
                    <SelectValue placeholder="Select script" />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]">
                    {filteredScripts.map((s: any) => (
                      <SelectItem
                        key={s.id}
                        value={String(s.id)}
                        className="text-xs"
                        data-testid={`select-floating-script-${s.id}`}
                      >
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium truncate">
                  {activeScript.name}
                </span>
                <Badge
                  variant="secondary"
                  className="text-[10px] px-1.5 py-0 shrink-0"
                >
                  {badgeLabel}
                </Badge>
              </div>
              <div
                className="overflow-y-auto text-xs leading-relaxed text-foreground pr-1 prose prose-sm dark:prose-invert max-w-none"
                style={{ maxHeight: contentMaxHeight }}
                data-testid="text-floating-script-content"
                dangerouslySetInnerHTML={{ __html: renderedContent }}
              />
            </>
          )}
        </CardContent>

        {/* Resize handle */}
        <div
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
          onMouseDown={handleResizeMouseDown}
          data-testid="floating-call-script-resize-handle"
          style={{ zIndex: 1 }}
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            className="absolute bottom-1 right-1 text-muted-foreground/50"
            fill="currentColor"
          >
            <rect x="6" y="0" width="2" height="10" />
            <rect x="0" y="6" width="10" height="2" />
          </svg>
        </div>
      </Card>
    </div>
  );
}
